import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { IVideo } from '../../pages/admin/videos/hooks/useFetchVideos';
import { fetchVideoPage, PAGE_SIZE } from '../../pages/admin/videos/adminVideosV2/helper';

/**
 * The section's owned query model. Replaces per-mount `usePagination`
 * `ignoreParams` lists with one rule: everything in the URL except
 * page/video/view/selectedTab/limit/sort is part of the query identity —
 * including proId, so switching experts resets paging (old §8.14 bug).
 * Deep-link `?page=N` loads as ONE ranged fetch (no 300ms backfill walk).
 */
interface Options {
  brandId?: number;
  legacyMode?: boolean;
  sort?: string;
  enabled?: boolean;
  /** Admin "Deleted" view (role-checked by the caller): soft-deleted rows only. */
  deleted?: boolean;
}

const OWNED = ['page', 'video', 'view', 'selectedTab', 'limit', 'sort'];
/** Ceiling for the deep-link ranged fetch: `?page=5000` must not become
 *  `limit=100000`. Beyond this the user gets the first MAX_DEEP_LINK_PAGES
 *  pages and can Load More from there. */
const MAX_DEEP_LINK_PAGES = 10;

export function useVideosQuery({ brandId, legacyMode, sort, enabled = true, deleted = false }: Options) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [videos, setVideos] = useState<IVideo[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const queryKey = useMemo(() => {
    const p = new URLSearchParams(searchParams);
    OWNED.forEach((k) => p.delete(k));
    p.sort();
    return `${p.toString()}|${brandId ?? ''}|${legacyMode ? 1 : 0}|${sort ?? ''}|${deleted ? 1 : 0}`;
  }, [searchParams, brandId, legacyMode, sort, deleted]);

  const requestSeq = useRef(0);
  const lastKey = useRef<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const load = useCallback(
    async (pages: number) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      const seq = ++requestSeq.current;
      setLoading(true);
      setError(null);
      const result = await fetchVideoPage(
        new URLSearchParams(window.location.search),
        1,
        legacyMode,
        brandId,
        sort,
        Math.max(1, pages) * PAGE_SIZE,
        undefined,
        controller.signal,
        deleted
      );
      // No `finally`: if a newer load superseded us, IT owns `loading` and
      // clears it; loadMore can't supersede a load because the Load More
      // button is unmounted while `loading` renders skeletons.
      if (seq !== requestSeq.current) return;
      setVideos(result.rows);
      setTotalCount(result.count);
      setError(result.error);
      setLoading(false);
    },
    [brandId, legacyMode, sort, deleted]
  );

  // Same fetch as `load`, minus the loading flip. Used by an upload completing
  // on a card: `loading` swaps the whole list for skeletons, which would unmount
  // the uploading card mid-flight and destroy its progress overlay + watchdog.
  // Ref-backed on purpose: this fires 30-90s after a file is dropped, so a
  // captured snapshot would refetch with whatever was live AT DROP TIME.
  // Two real failures that caused: Load More during an upload got truncated
  // back to the old row count, and changing sort/non-project mid-upload let
  // the stale refresh abort the new load and win with old-parameter rows,
  // leaving the list permanently disagreeing with the controls.
  // Committed in an effect, not assigned during render: an abandoned
  // concurrent render must not leave its props here for a later refresh to
  // fetch with.
  const latest = useRef({ len: 0, sort, legacyMode, brandId, deleted, busy: false });
  useEffect(() => {
    latest.current = {
      len: videos.length,
      sort,
      legacyMode,
      brandId,
      deleted,
      busy: loading || loadingMore,
    };
  });

  const refreshSilently = useCallback(async () => {
    const { len, sort: s, legacyMode: lm, brandId: bid, deleted: del, busy } = latest.current;
    // A load or loadMore is already in flight and will deliver fresher rows
    // than we would. Critically, we must NOT abort it: loadMore shares
    // abortRef and requestSeq, so cancelling it would swallow the user's
    // Load More click with no spinner and no rows.
    if (busy) return;
    // Deliberately does not abort or bump requestSeq — a background refresh
    // must never cancel or supersede a user-initiated request. That also means
    // it never has to clear `loading`, because it never strands a load.
    const seqAtStart = requestSeq.current;
    const pages = Math.max(1, Math.ceil(len / PAGE_SIZE));
    const result = await fetchVideoPage(
      new URLSearchParams(window.location.search),
      1,
      lm,
      bid,
      s,
      pages * PAGE_SIZE,
      undefined,
      undefined,
      del
    );
    // Something the user started took over while we were in flight; it owns
    // the list now.
    if (seqAtStart !== requestSeq.current) return;
    // Never trade a good list for an empty one because a background refresh
    // failed — loadMore guards the same way.
    if (result.error) return;
    setVideos(result.rows);
    setTotalCount(result.count);
  }, []);

  useEffect(() => {
    if (!enabled) return;
    const first = lastKey.current === null;
    if (lastKey.current === queryKey) return;
    lastKey.current = queryKey;
    if (first) {
      // Deep link: honor ?page=N with one ranged fetch.
      const urlPage = parseInt(new URLSearchParams(window.location.search).get('page') || '1', 10) || 1;
      load(Math.min(Math.max(1, urlPage), MAX_DEEP_LINK_PAGES));
      return;
    }
    // Query changed (search included — ListPageHeader debounces its URL
    // writes, so typing lands here once per pause): back to page 1.
    setSearchParams(
      (params) => {
        params.delete('page');
        return params;
      },
      { replace: true }
    );
    load(1);
  }, [queryKey, enabled, load, setSearchParams]);

  const hasMore = videos.length < totalCount;

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    const offset = videos.length;
    const nextPage = Math.floor(offset / PAGE_SIZE) + 1;
    setLoadingMore(true);
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const seq = ++requestSeq.current;
    try {
      const result = await fetchVideoPage(
        new URLSearchParams(window.location.search),
        nextPage,
        legacyMode,
        brandId,
        sort,
        undefined,
        offset,
        controller.signal,
        deleted
      );
      // A newer request superseded this one — drop the payload, but still fall
      // through to `finally` so the button never sticks on "Loading…".
      if (seq !== requestSeq.current) return;
      if (!result.error) {
        setError(null);
        setVideos((prev) => [...prev, ...result.rows]);
        setTotalCount(result.count);
        setSearchParams(
          (params) => {
            params.set('page', String(nextPage));
            return params;
          },
          { replace: true }
        );
      } else {
        setError(result.error);
      }
    } finally {
      setLoadingMore(false);
    }
  }, [videos.length, hasMore, loadingMore, brandId, legacyMode, sort, deleted, setSearchParams]);

  const retry = useCallback(() => {
    load(Math.max(1, Math.ceil(videos.length / PAGE_SIZE)));
  }, [load, videos.length]);

  return { videos, totalCount, loading, loadingMore, error, hasMore, loadMore, retry, refreshSilently };
}
